import type {
	OrderClaimDetailResponseDto,
	OrderDetailResponseDto,
	OrderExaminationResponseDto,
	OrderPriceResponseDto,
	OrderSetupDetailResponseDto,
} from "Infrastructure/Api/Api";
import { Resources } from "Infrastructure/Translation/Resources";
import { OrderDetailTab } from "Order/Pages/Detail/Tabs/OrderDetailTab";
import { OrderEditTab } from "Order/Pages/Detail/Tabs/OrderEditTab";
import { OrderExaminationTab } from "Order/Pages/Detail/Tabs/OrderExaminationTab";
import { OrderInvoiceTab } from "Order/Pages/Detail/Tabs/OrderInvoiceTab";
import { OrderMorningCheckTab } from "Order/Pages/Detail/Tabs/OrderMorningCheckTab";
import { OrderPaymentTab } from "Order/Pages/Detail/Tabs/OrderPaymentTab";
import { OrderProtocolTab } from "Order/Pages/Detail/Tabs/OrderProtocolTab";
import { OrderReviewTab } from "Order/Pages/Detail/Tabs/OrderReviewTab";
import { OrderPaths } from "Order/Routes/Routing";
import type { TabType } from "Shared/Components/Tabs/Types/TabType";
import {
	Assignment,
	CreditCard,
	Edit,
	FactCheck,
	FireExtinguisher,
	InsertDriveFile,
	Quiz,
	RequestQuote,
	SentimentSatisfied,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import type { TOptions } from "i18next";
import type { ObjPathProxy } from "ts-object-path";
import { OrderClaimTab } from "../OrderClaimTab";

export enum OrderDetailTabs {
	detail = "detail",
	edit = "edit",
	invoice = "invoice",
	protocol = "protocol",
	payment = "payment",
	examination = "examination",
	review = "review",
	claim = "claim",
	morningCheck = "morning-check",
}

const orderResource = Resources.base.order.shared;
const claimResource = Resources.base.claim.claimsTabs;

export const getOrderDetailTabs = (
	orderId: number,
	t: (
		resourcePath: ObjPathProxy<unknown, string>,
		options?: TOptions,
	) => string,
	invoiceId?: number | null,
	hasExamination?: boolean,
	claims?: OrderClaimDetailResponseDto[],
): TabType[] => {
	const tabs: TabType[] = [
		{
			index: OrderDetailTabs.detail,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<InsertDriveFile fontSize="small" />
					<Typography>{t(orderResource.detail)}</Typography>
				</Stack>
			),
			path: OrderPaths.Detail.path(orderId),
			tab: (order: OrderDetailResponseDto) => <OrderDetailTab order={order} />,
		},
		{
			index: OrderDetailTabs.edit,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<Edit fontSize="small" />
					<Typography>{t(orderResource.edit)}</Typography>
				</Stack>
			),
			path: OrderPaths.Edit.path(orderId),
			tab: (
				order: OrderDetailResponseDto,
				orderPrice: OrderPriceResponseDto,
				orderSetup?: OrderSetupDetailResponseDto,
			) => (
				<OrderEditTab
					orderSetup={orderSetup}
					order={order}
					orderPrice={orderPrice}
				/>
			),
		},
		{
			index: OrderDetailTabs.payment,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<CreditCard fontSize="small" />
					<Typography>{t(orderResource.balance)}</Typography>
				</Stack>
			),
			path: OrderPaths.Payment.path(orderId),
			tab: (order: OrderDetailResponseDto) => <OrderPaymentTab order={order} />,
		},
		{
			index: OrderDetailTabs.review,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<SentimentSatisfied fontSize="small" />
					<Typography>{t(orderResource.review)}</Typography>
				</Stack>
			),
			path: OrderPaths.Review.path(orderId),
			tab: (order: OrderDetailResponseDto) => <OrderReviewTab order={order} />,
		},
		{
			index: OrderDetailTabs.morningCheck,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<FactCheck fontSize="small" />
					<Typography>Morning Check</Typography>
				</Stack>
			),
			path: OrderPaths.MorningCheck.path(orderId),
			tab: (order: OrderDetailResponseDto) => (
				<OrderMorningCheckTab order={order} />
			),
		},
	];

	if (claims && claims?.length > 0) {
		tabs.push({
			index: OrderDetailTabs.claim,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<FireExtinguisher fontSize="small" />
					<Typography>{t(claimResource.tabTitle)}</Typography>
				</Stack>
			),
			path: OrderPaths.Claim.path(orderId),
			tab: (order: OrderDetailResponseDto) => <OrderClaimTab order={order} />,
		});
	}

	if (invoiceId) {
		tabs.push({
			index: OrderDetailTabs.invoice,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<RequestQuote fontSize="small" />
					<Typography>{t(orderResource.invoice)}</Typography>
				</Stack>
			),
			path: OrderPaths.Invoice.path(orderId),
			tab: (order: OrderDetailResponseDto) => {
				if (!order.invoiceId) {
					return <Typography>No invoice available</Typography>;
				}

				return <OrderInvoiceTab invoiceId={order.invoiceId} />;
			},
		});
	}

	tabs.push({
		index: OrderDetailTabs.protocol,
		label: (
			<Stack flexDirection="row" alignItems="center" gap={1}>
				<Assignment fontSize="small" />
				<Typography>{t(orderResource.protocol)}</Typography>
			</Stack>
		),
		path: OrderPaths.Protocol.path(orderId),
		tab: (order: OrderDetailResponseDto) => (
			<OrderProtocolTab orderId={order.id} />
		),
	});

	if (hasExamination) {
		tabs.push({
			index: OrderDetailTabs.examination,
			label: (
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<Quiz fontSize="small" />
					<Typography>{t(orderResource.examination)}</Typography>
				</Stack>
			),
			path: OrderPaths.Examination.path(orderId),
			tab: (
				_order: OrderDetailResponseDto,
				_orderPrice?: OrderPriceResponseDto,
				_orderSetup?: OrderSetupDetailResponseDto,
				orderExamination?: OrderExaminationResponseDto,
			) => (
				<OrderExaminationTab
					orderExamination={orderExamination}
					order={_order}
				/>
			),
		});
	}

	return tabs;
};
